import "./index.css";
import variants from "../variants.json";
import React, { useMemo } from "react";

const CopyLink = ({ href }: { href: string }) => {
  const [copied, setCopied] = React.useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(href);
    setCopied(true);
    setTimeout(() => {
      setCopied(false);
    }, 1000);
  };

  return (
    <span
      onClick={handleCopy}
      className=" text-sm hover:text-blue-500 cursor-pointer"
    >
      {copied ? "Copied" : "Copy link"}
    </span>
  );
};

const VideoVariant = ({ variant }: { variant: (typeof variants)[number] }) => {
  return (
    <div className="bg-white p-2 mb-2 border-2 border-b-4 rounded-md">
      <a
        href={`/${variant.fileNames[0]}`}
        className="leading-tight hover:text-blue-500"
      >
        <strong className="mr-2 inline-block font-brand">
          {variant.fileNames[0]}
        </strong>
      </a>
      <div className="flex-1"></div>
      <div className="text-neutral-500 text-sm font-brand">
        {(variant.size / 1024 / 1024).toFixed(1)} MB {" ∙ "}
        {variant.container.toUpperCase()}
        {" ∙ "}
        {variant.videoCodec === "none"
          ? "No video"
          : variant.videoCodec.toUpperCase()}
        {" ∙ "}
        {variant.audioCodec === "none"
          ? "No audio"
          : variant.audioCodec.toUpperCase()}
        {" ∙ "}
        <CopyLink href={`https://remotion.media/${variant.fileNames[0]}`} />
        {" ∙ "}
        <a
          href={`/${variant.fileNames[0]}`}
          download={variant.fileNames[0]}
          className=" text-sm hover:text-blue-500 cursor-pointer"
        >
          Download
        </a>
        {" ∙ "}
        <a
          href={`https://remotion.dev/convert?url=${encodeURIComponent(
            `https://remotion.media/${variant.fileNames[0]}`
          )}`}
          download={variant.fileNames[0]}
          className=" text-sm hover:text-blue-500 cursor-pointer"
        >
          Probe
        </a>
      </div>
      {variant.fileNames.length > 1 && (
        <div className="text-neutral-500 text-sm font-brand">
          <span>Aliases: </span>
          {variant.fileNames.slice(1).map((fileName) => (
            <span key={fileName} className="mr-2">
              {fileName}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

const CategoryGroup = ({
  category,
  variants: v,
}: {
  category: string;
  variants: (typeof variants)[number][];
}) => {
  const categoryLabel = useMemo(() => {
    if (category === "codecs") return "Codecs";
    if (category === "dimensions") return "Dimensions";
    if (category === "durations") return "Durations";
    if (category === "audio") return "Audio";
    if (category === "fps") return "FPS";
    if (category === "sample-rates") return "Sample Rates";

    return category;
  }, [category]);

  return (
    <div>
      <h3 className="text-lg font-bold mt-5 mb-4 font-brand">
        {categoryLabel}
      </h3>
      {v.map((variant) => (
        <VideoVariant key={variant.fileNames[0]} variant={variant} />
      ))}
    </div>
  );
};

const groups = variants.reduce((acc, variant) => {
  acc[variant.category] = acc[variant.category] || [];
  acc[variant.category].push(variant);
  return acc;
}, {} as Record<string, (typeof variants)[number][]>);

export function App() {
  return (
    <div className="max-w-[700px] w-full m-auto mt-4 pl-4 pr-4 mb-20">
      <h2 className="text-2xl font-bold mt-10 mb-2 font-brand">
        Audio and video files for testing
      </h2>
      <p className="text-neutral-500 font-brand mt-1 mb-8">
        All files are available on{" "}
        <code className="font-brand text-brand">remotion.media/[filename]</code>
        .
        <br />
        Hosted on Cloudflare R2 Free Tier, allowing for theoretically unlimited
        bandwidth. <br />
        Files may be used royalty-free and without attribution.
      </p>
      {Object.entries(groups).map(([category, variants]) => (
        <CategoryGroup key={category} category={category} variants={variants} />
      ))}
    </div>
  );
}

export default App;
